import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export const sectionTitle = "text-[clamp(2.125rem,4.4vw,3.75rem)] leading-[1.02] font-medium tracking-[-0.04em]";
/** Big figures: prices, times, counts. */
export const figure = "font-medium leading-none tracking-[-0.04em] tabular-nums";

/** One band of the page. Sections share the wide column and the same breathing room above. */
export const Section = ({ id, children, className }: { id?: string; children: ReactNode; className?: string }) => (
  <section id={id} className={cn("scroll-mt-6 pt-16 md:pt-[104px]", className)}>
    {children}
  </section>
);

/** A section's title on the left, with one line or one action on the right. */
export const SectionHead = ({ title, aside }: { title: ReactNode; aside?: ReactNode }) => (
  <div className="mb-7 flex flex-col items-start justify-between gap-4 px-4 md:flex-row md:items-end md:gap-6">
    <h2 className={sectionTitle}>{title}</h2>
    {aside ? <div className="max-w-[340px] text-sm text-muted-foreground md:text-right">{aside}</div> : null}
  </div>
);

/** The site's surface: a borderless slab one step above the page. */
export const card = "relative isolate overflow-hidden rounded-[28px] bg-card p-6 md:p-7";
/** The one loud card per view: solid primary, dark text. */
export const primaryCard =
  "relative isolate overflow-hidden rounded-[28px] bg-primary p-6 text-primary-foreground md:p-7";
/** A recessed block inside a card. */
export const well = "rounded-[20px] bg-[var(--site-well)]";
export const label = "text-[13px] text-muted-foreground";

const buttonBase =
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-sm font-semibold whitespace-nowrap outline-none transition-[background-color,filter,transform] focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.97]";
export const primaryButton = cn(buttonBase, "h-[46px] bg-primary px-6 text-primary-foreground hover:brightness-105");
/** The button that sits on a primary card. */
export const inkButton = cn(buttonBase, "h-[46px] bg-primary-foreground px-6 text-primary hover:brightness-125");
export const ghostButton = cn(buttonBase, "h-[46px] bg-[var(--site-well)] px-6 hover:bg-muted");
export const smallButton = "h-9 px-4 text-[13px]";

/** A round icon button. `primaryOrb` is the one to tap. */
export const orb =
  "grid size-[46px] shrink-0 place-items-center rounded-full bg-[var(--site-well)] outline-none transition-[background-color,filter,transform] hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.96]";
export const primaryOrb =
  "grid size-[46px] shrink-0 place-items-center rounded-full bg-primary text-primary-foreground outline-none transition-[filter,transform] hover:brightness-105 focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.96]";

/** A filter or a tab. Lit when `aria-pressed` or `aria-current` says so. */
export const pill =
  "inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-full bg-[var(--site-well)] px-[18px] text-[13px] font-medium whitespace-nowrap outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring aria-pressed:bg-primary aria-pressed:font-semibold aria-pressed:text-primary-foreground";
/** A small accent label on a card. */
export const tag =
  "inline-flex h-[26px] items-center gap-1.5 self-start rounded-full bg-primary/15 px-2.5 text-xs font-semibold text-[var(--site-ink)]";
/** The same label on a primary card. */
export const inkTag =
  "inline-flex h-[26px] items-center gap-1.5 self-start rounded-full bg-primary-foreground/10 px-2.5 text-xs font-semibold text-primary-foreground";

/**
 * Column count for a row of people, by how many there are: one, two or three fill the row
 * instead of leaving an empty seat, four or more wrap in fours.
 */
export const peopleColumns = (count: number) =>
  count >= 4 ? "lg:grid-cols-4" : count === 3 ? "lg:grid-cols-3" : count === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1";
